import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tree, TreeNode } from "react-organizational-chart";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmpAvatar } from "@/components/EmpAvatar";
import { PageTitle } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ZoomIn, ZoomOut, Maximize, Search, FileImage, FileDown, Link2, ChevronUp, ChevronDown, ListTree } from "lucide-react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

const LINE_COLOR = "hsl(var(--border))";
const HL_COLOR = "hsl(var(--primary))";

function Card({ node, onClick, q, childCount, isCollapsed, onToggle, pathSet }) {
  const hay = `${node.alias || ""} ${node.full_name || ""}`.toLowerCase();
  const isMatch = q && hay.includes(q.toLowerCase());
  const onPath = pathSet ? pathSet.has(node.id) : false;
  const dim = pathSet && !onPath;
  const divisions = node.divisions?.length ? node.divisions : node.division ? [node.division] : [];
  const linked = node.linked;
  const hasKids = childCount > 0;
  const ring = isMatch
    ? "!border-destructive ring-2 ring-destructive/50"
    : onPath
    ? "!border-primary ring-2 ring-primary/40"
    : "";
  return (
    <div
      onClick={() => onClick(node.id)}
      data-testid={`org-node-${node.id}${linked ? "-linked" : ""}`}
      className={`relative inline-flex items-center gap-3 rounded-md px-4 py-3 min-w-[220px] max-w-[240px] cursor-pointer text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        linked
          ? "bg-muted/40 border border-dashed border-primary/50"
          : "bg-card border border-border"
      } ${ring} ${dim ? "opacity-25 saturate-50" : "opacity-100"}`}
    >
      <EmpAvatar emp={node} size="h-11 w-11" />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-heading font-bold text-sm truncate">{node.alias || node.full_name}</p>
          {node.manager_count > 1 && <Link2 className="h-3.5 w-3.5 text-primary shrink-0" title="Reports to more than one manager" />}
        </div>
        <p className="text-xs text-muted-foreground truncate">{node.position || node.role}</p>
        {linked ? (
          <p className="overline truncate text-primary/70">Also reports to · Primary: {node.primary_manager}</p>
        ) : (
          <>
            {node.department && <p className="overline truncate">{node.department}</p>}
            {divisions.map((d, i) => (
              <p key={i} className="overline truncate text-primary/70">{d}</p>
            ))}
          </>
        )}
      </div>
      {hasKids && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
          data-testid={`org-toggle-${node.id}`}
          title={isCollapsed ? `Expand ${childCount} report${childCount > 1 ? "s" : ""}` : "Collapse team"}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 h-6 min-w-6 px-1.5 rounded-full border border-border bg-background text-foreground shadow-sm flex items-center justify-center gap-0.5 text-xs font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          {isCollapsed ? (<><span>{childCount}</span><ChevronDown className="h-3.5 w-3.5" /></>) : (<ChevronUp className="h-3.5 w-3.5" />)}
        </button>
      )}
    </div>
  );
}

function gridCols(n) {
  if (n <= 3) return n;
  if (n <= 8) return Math.ceil(n / 2); // 2 rows
  return Math.ceil(n / 3); // 3 rows
}

const GAP_X = 24; // matches gap-x-6
const BUS_Y = 20; // horizontal bus offset from the top of the grid

// Compact grid of leaf reports that still draws a connector line from the
// manager to EVERY card: a trunk from the top-center down to a horizontal bus,
// one vertical spine per column, then an elbow into each card's left edge.
function LeafGrid({ leaves, cols, ctx }) {
  const wrapRef = useRef(null);
  const cardRefs = useRef([]);
  const [svg, setSvg] = useState(null);
  const pathSet = ctx.pathSet;
  const pathKey = pathSet ? [...pathSet].join(",") : "";

  useLayoutEffect(() => {
    const measure = () => {
      const cont = wrapRef.current;
      if (!cont) return;
      const items = cardRefs.current
        .map((el, i) => (el ? { left: el.offsetLeft, top: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight, on: pathSet ? pathSet.has(leaves[i]?.id) : false } : null))
        .filter(Boolean);
      if (!items.length) return;
      // group into columns (row-major fill)
      const columns = [];
      for (let c = 0; c < cols; c++) {
        const col = [];
        for (let i = c; i < items.length; i += cols) col.push(items[i]);
        if (col.length) columns.push(col);
      }
      const spines = columns.map((col) => ({
        x: Math.min(...col.map((cd) => cd.left)) - GAP_X / 2,
        cards: col,
      }));
      const xs = spines.map((s) => s.x);
      const cx = cont.offsetWidth / 2;
      const base = [];
      const hl = [];
      const anyOn = items.some((it) => it.on);
      (anyOn ? hl : base).push(`M ${cx} 0 V ${BUS_Y}`); // trunk
      (anyOn ? hl : base).push(`M ${Math.min(...xs)} ${BUS_Y} H ${Math.max(...xs)}`); // bus
      spines.forEach((s) => {
        const bottom = Math.max(...s.cards.map((cd) => cd.top + cd.h / 2));
        const colOn = s.cards.some((cd) => cd.on);
        (colOn ? hl : base).push(`M ${s.x} ${BUS_Y} V ${bottom}`); // vertical spine
        s.cards.forEach((cd) => {
          const yc = cd.top + cd.h / 2;
          (cd.on ? hl : base).push(`M ${s.x} ${yc} H ${cd.left}`); // elbow into card
        });
      });
      setSvg({ w: cont.offsetWidth, h: cont.offsetHeight, base: base.join(" "), hl: hl.join(" ") });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [leaves, cols, pathKey]);

  return (
    <div ref={wrapRef} className="relative" style={{ paddingTop: BUS_Y, paddingLeft: GAP_X }} data-testid="org-leaf-grid">
      {svg && (
        <svg className="absolute inset-0 pointer-events-none" width={svg.w} height={svg.h} style={{ overflow: "visible" }}>
          {svg.base && <path d={svg.base} fill="none" stroke={LINE_COLOR} strokeWidth="2" strokeLinejoin="round" className={pathSet ? "opacity-25" : ""} />}
          {svg.hl && <path d={svg.hl} fill="none" stroke={HL_COLOR} strokeWidth="2.5" strokeLinejoin="round" />}
        </svg>
      )}
      <div className="grid gap-x-6 gap-y-8 justify-items-start" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {leaves.map((l, i) => (
          <div key={l.id} ref={(el) => (cardRefs.current[i] = el)} className="relative z-10">
            <Card node={l} onClick={ctx.onClick} q={ctx.q} childCount={0} isCollapsed={false} onToggle={ctx.onToggle} pathSet={ctx.pathSet} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Render a node's children: reports who have their own team stay as normal
// branches; leaf reports are packed into a compact 2–3 row grid (with a
// connector to each card) so wide flat teams don't stretch the chart.
function renderChildren(children, ctx) {
  const branches = children.filter((c) => (c.children || []).length);
  const leaves = children.filter((c) => !(c.children || []).length);
  const out = branches.map((c) => renderNode(c, ctx));
  if (leaves.length >= 4) {
    out.push(<TreeNode key="__leafgrid" label={<LeafGrid leaves={leaves} cols={gridCols(leaves.length)} ctx={ctx} />} />);
  } else {
    leaves.forEach((l) => out.push(renderNode(l, ctx)));
  }
  return out;
}

function renderNode(node, ctx) {
  const children = node.children || [];
  const collapsed = ctx.collapsed.has(node.id);
  const label = (
    <Card node={node} onClick={ctx.onClick} q={ctx.q} childCount={children.length} isCollapsed={collapsed} onToggle={ctx.onToggle} pathSet={ctx.pathSet} />
  );
  const kids = collapsed ? [] : renderChildren(children, ctx);
  return (
    <TreeNode key={node.id} label={label}>
      {kids}
    </TreeNode>
  );
}

function renderTree(root, ctx) {
  const children = root.children || [];
  const collapsed = ctx.collapsed.has(root.id);
  const rootLabel = (
    <Card node={root} onClick={ctx.onClick} q={ctx.q} childCount={children.length} isCollapsed={collapsed} onToggle={ctx.onToggle} pathSet={ctx.pathSet} />
  );
  const kids = collapsed ? [] : renderChildren(children, ctx);
  return (
    <Tree key={root.id} lineWidth="2px" lineColor={LINE_COLOR} lineBorderRadius="8px" label={rootLabel}>
      {kids}
    </Tree>
  );
}

function collectParentIds(nodes, acc) {
  for (const n of nodes) {
    if ((n.children || []).length) {
      acc.add(n.id);
      collectParentIds(n.children, acc);
    }
  }
  return acc;
}

export default function OrgChart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const chartRef = useRef(null);
  const [roots, setRoots] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const storageKey = user?.id ? `orgchart-collapsed-${user.id}` : null;

  useEffect(() => {
    api.get("/org-chart").then((r) => setRoots(r.data)).catch(() => {});
  }, []);

  // Remember each viewer's collapsed branches across visits.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      setCollapsed(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch { setCollapsed(new Set()); }
  }, [storageKey]);
  const persist = (set) => { if (storageKey) { try { localStorage.setItem(storageKey, JSON.stringify([...set])); } catch {} } };

  // Map every employee to their managers (all of them) so a search can light
  // up the full reporting path to the top, including a second manager.
  const managersOf = useMemo(() => {
    const m = {};
    const walk = (n) => {
      if (!n.linked) m[n.id] = n.reports_to || [];
      (n.children || []).forEach(walk);
    };
    roots.forEach(walk);
    return m;
  }, [roots]);

  // Set of node ids on the reporting path of the searched name (matched people
  // + all of their ancestors up both manager chains). null when not searching.
  const pathSet = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return null;
    const set = new Set();
    const queue = [];
    const nameOf = {};
    const walk = (n) => { if (!n.linked) nameOf[n.id] = `${n.alias || ""} ${n.full_name || ""}`.toLowerCase(); (n.children || []).forEach(walk); };
    roots.forEach(walk);
    for (const [id, name] of Object.entries(nameOf)) {
      if (name.includes(term)) { set.add(id); queue.push(id); }
    }
    while (queue.length) {
      const id = queue.shift();
      for (const mid of managersOf[id] || []) {
        if (!set.has(mid)) { set.add(mid); queue.push(mid); }
      }
    }
    return set.size ? set : new Set(["__none__"]);
  }, [q, roots, managersOf]);

  // While searching, temporarily expand collapsed branches on the path so the
  // matched person is visible (without changing the saved layout).
  const effectiveCollapsed = useMemo(() => {
    if (!pathSet) return collapsed;
    return new Set([...collapsed].filter((id) => !pathSet.has(id)));
  }, [collapsed, pathSet]);

  const onClick = (id) => navigate(`/employees/${id}`);
  const onToggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    persist(next);
    return next;
  });
  const collapseAll = () => { const next = collectParentIds(roots, new Set()); setCollapsed(next); persist(next); };
  const expandAll = () => { const next = new Set(); setCollapsed(next); persist(next); };
  const ctx = { onClick, onToggle, collapsed: effectiveCollapsed, q, pathSet };

  const capture = async () => {
    const prev = zoom;
    setZoom(1);
    await new Promise((r) => setTimeout(r, 350));
    const dataUrl = await toPng(chartRef.current, { backgroundColor: "#ffffff", pixelRatio: 2, cacheBust: true });
    setZoom(prev);
    return dataUrl;
  };

  const exportPng = async () => {
    if (!chartRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await capture();
      const a = document.createElement("a");
      a.href = dataUrl; a.download = "org-chart.png"; a.click();
      toast.success("Org chart exported as PNG");
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    if (!chartRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await capture();
      const img = new Image();
      img.src = dataUrl;
      await new Promise((r) => { img.onload = r; });
      const pdf = new jsPDF({ orientation: img.width > img.height ? "landscape" : "portrait", unit: "px", format: [img.width, img.height] });
      pdf.addImage(dataUrl, "PNG", 0, 0, img.width, img.height);
      pdf.save("org-chart.pdf");
      toast.success("Org chart exported as PDF");
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Company Structure" title="Organization Chart">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Highlight employee…" className="pl-9 w-48" data-testid="org-search" />
        </div>
        <Button variant="outline" size="icon" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} data-testid="zoom-out"><ZoomOut className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" onClick={() => setZoom(1)} data-testid="zoom-reset"><Maximize className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} data-testid="zoom-in"><ZoomIn className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" onClick={collapsed.size ? expandAll : collapseAll} data-testid="collapse-toggle-all"><ListTree className="h-4 w-4 mr-1.5" /> {collapsed.size ? "Expand all" : "Collapse all"}</Button>
        {isAdmin && <Button variant="outline" size="sm" onClick={exportPng} disabled={exporting} data-testid="export-png"><FileImage className="h-4 w-4 mr-1.5" /> PNG</Button>}
        {isAdmin && <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="export-pdf"><FileDown className="h-4 w-4 mr-1.5" /> PDF</Button>}
      </PageTitle>

      <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground" data-testid="org-legend">
        <span className="flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5 text-primary" /> Reports to more than one manager</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-5 h-3 rounded-sm border border-dashed border-primary/50 bg-muted/40" /> Secondary (dotted-line) reporting</span>
      </div>

      <div className="border border-border rounded-md bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:20px_20px] overflow-auto p-10 min-h-[60vh]">
        <div
          ref={chartRef}
          className="w-max origin-top-left transition-transform duration-200 p-4 space-y-12"
          style={{ transform: `scale(${zoom})` }}
        >
          {roots.map((root) => renderTree(root, ctx))}
        </div>
      </div>
    </div>
  );
}
